import type { ComponentChildren } from 'preact';
import type { WidgetDefinition } from '../../widgets';
import { catalogEntry, missingRequiredFields } from '../palette/widgetCatalog';
import { Field, Section, TextField, patcher } from './fields';
import { MomentaryButtonEditor } from './editors/MomentaryButtonEditor';
import { ToggleEditor } from './editors/ToggleEditor';
import { EnumSetterEditor } from './editors/EnumSetterEditor';
import { SetpointStepperEditor } from './editors/SetpointStepperEditor';
import { SliderEditor } from './editors/SliderEditor';
import { LockGuardEditor } from './editors/LockGuardEditor';
import { GaugeEditor } from './editors/GaugeEditor';
import { RollingCounterEditor } from './editors/RollingCounterEditor';
import { StatusLedEditor } from './editors/StatusLedEditor';
import { ValueTileEditor } from './editors/ValueTileEditor';
import { AlarmIndicatorEditor } from './editors/AlarmIndicatorEditor';
import { SparklineEditor } from './editors/SparklineEditor';
import { TrendEditor } from './editors/TrendEditor';
import { SchematicOverlayEditor } from './editors/SchematicOverlayEditor';
import { EnergyFlowEditor } from './editors/EnergyFlowEditor';
import { ScheduleEditorEditor } from './editors/ScheduleEditorEditor';
import { StageCascadeEditor } from './editors/StageCascadeEditor';
import { HeatCircuitTileEditor } from './editors/HeatCircuitTileEditor';
import { HeatPumpKpiEditor } from './editors/HeatPumpKpiEditor';
import { HotWaterTileEditor } from './editors/HotWaterTileEditor';
import { RoomThermostatEditor } from './editors/RoomThermostatEditor';
import { PvSelfConsumptionEditor } from './editors/PvSelfConsumptionEditor';
import { WeatherTileEditor } from './editors/WeatherTileEditor';
import { SchematicPartEditor } from './editors/SchematicPartEditor';
import './PropertyPanel.css';
import {
  ActionButtonEditor, AlarmListEditor, DayScheduleEditor, DesignSections, DialEditor, HeatingCurveEditor, MonthBarsEditor, PowerFlowEditor, SplitBarEditor, TankEditor, HeroValueEditor, ImageEditor, NavBarEditor, ShapeEditor, SymbolEditor, TextEditor,
} from './editors/PanelEditors';

export interface PropertyPanelProps {
  /** Das fuehrende ausgewaehlte Widget, oder null, wenn nichts ausgewaehlt
   *  ist. */
  widget: WidgetDefinition | null;
  /**
   * Wie viele Widgets insgesamt ausgewaehlt sind.
   *
   * Nur zur Unterscheidung von "eines" und "mehrere" da; die anderen kommen
   * bewusst gar nicht herein, siehe unten.
   */
  selectionCount?: number;
  plantId: string;
  /** Shown instead of the properties when nothing is selected. The page
   *  decides what helps there; the panel does not know the editor. */
  placeholder?: ComponentChildren;
  onChange(next: WidgetDefinition): void;
}

/**
 * Eigenschaftsfeld des HMI-Editors.
 *
 * Kennt den Store nicht: rein kommt das ausgewaehlte Widget, raus geht ein
 * neues. Wer das Ergebnis wohin schreibt, entscheidet die Seite. Das ist
 * Absicht, weil derselbe Baustein spaeter auch in einer Vorschau oder einem
 * Dialog stehen soll, wo es keinen Editor-Store gibt.
 *
 * Aufbau: erst die gemeinsame Kopfzeile aus `WidgetBase`, dann der
 * typspezifische Teil. Fehlende Pflichtangaben werden oben gesammelt und im
 * Feld selbst markiert, aber nirgends erzwungen: der Editor ist Handarbeit,
 * und wer mitten in einer Kachel unterbrochen wird, muss seinen Stand
 * speichern koennen.
 */
export function PropertyPanel({
  widget,
  selectionCount = widget ? 1 : 0,
  plantId,
  placeholder,
  onChange,
}: PropertyPanelProps) {
  if (!widget) {
    return (
      <aside class="hv-pp hv-pp--empty">
        {placeholder ?? (
          <p class="hv-pp__placeholder">
            Kein Widget ausgewählt. Eines auf der Fläche anklicken, oder links
            in der Palette ein neues aufnehmen.
          </p>
        )}
      </aside>
    );
  }

  // Bei einer Mehrfachauswahl zeigt dieses Feld KEINE Eigenschaften, auch
  // nicht die gemeinsamen.
  //
  // Gemeinsam waeren nur die vier Felder aus `WidgetBase`, und genau die
  // taugen nicht dafuer. Eine Beschriftung benennt EIN Ding; sie auf sechs
  // Kacheln gleichzeitig zu setzen ist nie das, was jemand will. Rolle und
  // Plan waeren technisch moeglich, wuerden aber unsichtbar ueberschreiben,
  // was auf den einzelnen Widgets steht: das Feld zeigt dann einen Wert,
  // hinter dem sechs verschiedene lagen, und der Autor sieht den Verlust
  // nicht. Und ein Feld, das vier Angaben zeigt und zwanzig verschweigt,
  // luegt darueber, was gerade geaendert wird.
  //
  // Wer eines der sechs aendern will, klickt es an. Was an einer
  // Mehrfachauswahl wirklich gebraucht wird, ist Ausrichten und Angleichen,
  // und das steht ueber der Flaeche, wo es hingehoert.
  if (selectionCount > 1) {
    return (
      <aside class="hv-pp hv-pp--empty">
        <p class="hv-pp__placeholder">
          {selectionCount} Widgets ausgewählt. Eigenschaften werden einzeln
          gesetzt, deshalb steht hier nichts: wähl eines an, um es zu
          bearbeiten. Ausrichten und gleiche Größe liegen über der Fläche.
        </p>
      </aside>
    );
  }

  const entry = catalogEntry(widget.kind);
  const open = missingRequiredFields(widget);
  const missing = new Set(open.map((f) => f.field));

  return (
    <aside class="hv-pp">
      <header class="hv-pp__header">
        <h3 class="hv-pp__title">{entry.title}</h3>
        <p class="hv-pp__description">{entry.description}</p>
        <code class="hv-pp__id" title="Id im gespeicherten Projekt">{widget.id}</code>
      </header>

      {open.length > 0 && (
        <p class="hv-pp__open" role="status">
          Noch offen: {open.map((f) => f.label).join(', ')}. Speichern geht
          trotzdem.
        </p>
      )}

      <div class="hv-pp__body">
        <BaseSection widget={widget} onChange={onChange} />
        <TypedEditor widget={widget} plantId={plantId} missing={missing} onChange={onChange} />
        <DesignSections widget={widget} plantId={plantId} onChange={onChange} />
      </div>
    </aside>
  );
}

const LICENSES = [
  { value: '', label: 'Alle Pläne' },
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

/** Die Felder aus `WidgetBase`, gleich fuer alle 22 Typen. */
function BaseSection(props: {
  widget: WidgetDefinition;
  onChange(next: WidgetDefinition): void;
}) {
  const { widget } = props;
  const patch = patcher(widget, props.onChange);
  return (
    <Section title="Allgemein">
      <TextField
        label="Beschriftung"
        value={widget.label}
        placeholder="Ohne Beschriftung"
        hint="Leer lassen ist erlaubt: eine Leuchte oder ein Verlaufsstrich braucht oft keine."
        onChange={(next) => patch({ label: next })}
      />
      <TextField
        label="Sichtbar für Rolle"
        value={widget.requiresRole}
        placeholder="leer: für alle sichtbar"
        hint="Der Server erzwingt das unabhängig. Ein ausgeblendeter Knopf ist kein Schutz."
        onChange={(next) => patch({ requiresRole: next })}
      />
      <TextField
        label="Schreiben nur für Rolle"
        value={widget.requiresWriteRole}
        placeholder="leer: wie die Sichtbarkeits-Rolle"
        onChange={(next) => patch({ requiresWriteRole: next })}
      />
      <Field
        label="Ab Plan"
        hint="Das Widget wird unterhalb dieses Plans serverseitig aus der Antwort entfernt."
      >
        <select
          class="hv-pf__input"
          value={widget.requiresLicense ?? ''}
          onChange={(e) => {
            const raw = (e.currentTarget as HTMLSelectElement).value;
            patch({
              requiresLicense: raw === ''
                ? undefined
                : (raw as NonNullable<WidgetDefinition['requiresLicense']>),
            });
          }}
        >
          {LICENSES.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </Field>
    </Section>
  );
}

/**
 * Verteilung auf den typspezifischen Editor.
 *
 * Bewusst ein switch und keine Tabelle: so faellt ein neuer Widget-Typ bei
 * `tsc` auf, weil der Abschluss unten `never` verlangt. Eine Tabelle
 * `Record<WidgetKind, Komponente>` haette dasselbe geleistet, aber jeden
 * Eintrag eine Umtypung gekostet, und Umtypungen verdecken genau das, was
 * hier auffallen soll.
 */
function TypedEditor(props: {
  widget: WidgetDefinition;
  plantId: string;
  missing: ReadonlySet<string>;
  onChange(next: WidgetDefinition): void;
}) {
  const { widget, plantId, missing, onChange } = props;
  const shared = { plantId, missing, onChange };
  switch (widget.kind) {
    case 'momentary-button':
      return <MomentaryButtonEditor widget={widget} {...shared} />;
    case 'toggle':
      return <ToggleEditor widget={widget} {...shared} />;
    case 'enum-setter':
      return <EnumSetterEditor widget={widget} {...shared} />;
    case 'setpoint-stepper':
      return <SetpointStepperEditor widget={widget} {...shared} />;
    case 'slider':
      return <SliderEditor widget={widget} {...shared} />;
    case 'lock-guard':
      return <LockGuardEditor widget={widget} {...shared} />;
    case 'gauge':
      return <GaugeEditor widget={widget} {...shared} />;
    case 'rolling-counter':
      return <RollingCounterEditor widget={widget} {...shared} />;
    case 'status-led':
      return <StatusLedEditor widget={widget} {...shared} />;
    case 'value-tile':
      return <ValueTileEditor widget={widget} {...shared} />;
    case 'alarm-indicator':
      return <AlarmIndicatorEditor widget={widget} {...shared} />;
    case 'sparkline':
      return <SparklineEditor widget={widget} {...shared} />;
    case 'trend':
      return <TrendEditor widget={widget} {...shared} />;
    case 'schematic-overlay':
      return <SchematicOverlayEditor widget={widget} {...shared} />;
    case 'energy-flow':
      return <EnergyFlowEditor widget={widget} {...shared} />;
    case 'schedule-editor':
      return <ScheduleEditorEditor widget={widget} {...shared} />;
    case 'stage-cascade':
      return <StageCascadeEditor widget={widget} {...shared} />;
    case 'heat-circuit-tile':
      return <HeatCircuitTileEditor widget={widget} {...shared} />;
    case 'heat-pump-kpi':
      return <HeatPumpKpiEditor widget={widget} {...shared} />;
    case 'hot-water-tile':
      return <HotWaterTileEditor widget={widget} {...shared} />;
    case 'room-thermostat':
      return <RoomThermostatEditor widget={widget} {...shared} />;
    case 'pv-self-consumption':
      return <PvSelfConsumptionEditor widget={widget} {...shared} />;
    case 'weather-tile':
      return <WeatherTileEditor widget={widget} {...shared} />;
    case 'action-button':
      return <ActionButtonEditor widget={widget} {...shared} />;
    case 'text':
      return <TextEditor widget={widget} {...shared} />;
    case 'hero-value':
      return <HeroValueEditor widget={widget} {...shared} />;
    case 'image':
      return <ImageEditor widget={widget} {...shared} />;
    case 'shape':
      return <ShapeEditor widget={widget} {...shared} />;
    case 'symbol':
      return <SymbolEditor widget={widget} {...shared} />;
    case 'schematic-part':
      return <SchematicPartEditor widget={widget} {...shared} />;
    case 'nav-bar':
      return <NavBarEditor widget={widget} {...shared} />;
    case 'theme-toggle':
      return null;
    case 'user-invite':
      return null;
    case 'alarm-list':
      return <AlarmListEditor widget={widget} {...shared} />;
    case 'dial':
      return <DialEditor widget={widget} {...shared} />;
    case 'day-schedule':
      return <DayScheduleEditor widget={widget} {...shared} />;
    case 'heating-curve':
      return <HeatingCurveEditor widget={widget} {...shared} />;
    case 'tank':
      return <TankEditor widget={widget} {...shared} />;
    case 'power-flow':
      return <PowerFlowEditor widget={widget} {...shared} />;
    case 'month-bars':
      return <MonthBarsEditor widget={widget} {...shared} />;
    case 'split-bar':
      return <SplitBarEditor widget={widget} {...shared} />;
    default: {
      const unhandled: never = widget;
      void unhandled;
      return null;
    }
  }
}
